/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { config } from '../../config';
import { ConnectedStore } from '../../types/ConnectedStore';
import { Organization } from '../../types/Organization';
import ResponseError from '../../types/response/ResponseError';
import { SessionOrganization, SessionUser } from '../../types/SessionUser';
import { SetupSteps } from '../../services/helpers/guidedSetup';

interface CesSurvey {
    expiryTime?: number;
    show: boolean;
    tab?: boolean;
}

interface Surveys {
    [key: string]: CesSurvey;
}

export interface SessionState {
    token?: string;
    user?: SessionUser;
    expiryTime: number;
    nextRefresh?: number;
    globalError?: unknown;
    selectedOrganization: SessionOrganization;
    isFetchingUser?: boolean;
    organization?: string;
    availableStores: ConnectedStore[];
    isLoadingOrganization: boolean;
    showProcessingNotCompletedMessage: boolean;
    selectedOrganizationPending: boolean;
    hasSelectedOrganization: boolean;
    failedFetchingUser: boolean;
    cesSurveys: Surveys;
    guidedSetup: GuidedSetupState;
}

/** The guided setup of the user's own organization, as answered by GET organizations/{id}. */
export interface GuidedSetupState {
    /** Whether the user's own organization has been loaded (or failed to load) at least once. */
    isLoaded: boolean;
    shouldRedirectToGetStarted: boolean;
    setupSteps?: SetupSteps;
}

const initialGuidedSetup: GuidedSetupState = { isLoaded: false, shouldRedirectToGetStarted: false };

const initialState: SessionState = {
    token: undefined,
    user: {} as SessionUser,
    expiryTime: 0,
    nextRefresh: undefined,
    globalError: undefined,
    selectedOrganization: { id: '', name: '', blockInvites: false, maxApiKeys: 2 },
    isFetchingUser: true,
    organization: '',
    availableStores: [],
    isLoadingOrganization: false,
    showProcessingNotCompletedMessage: false,
    selectedOrganizationPending: false,
    failedFetchingUser: false,
    hasSelectedOrganization: false,
    cesSurveys: {},
    guidedSetup: initialGuidedSetup,
};
export const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        userAuthenticated: (
            state,
            action: PayloadAction<{
                token: string;
                expiryTime: number;
            }>
        ) => {
            state.token = action.payload.token;
            state.expiryTime = action.payload.expiryTime;
        },
        userLoggedOut: state => {
            state.token = undefined;
            state.user = {} as SessionUser;
            state.selectedOrganization = { id: '', name: '', blockInvites: false, maxApiKeys: 2 };
            state.failedFetchingUser = false;
            state.guidedSetup = initialGuidedSetup;
        },
        getUserSucceeded: (
            state,
            action: PayloadAction<{
                user: SessionUser;
                nextRefresh: number;
            }>
        ) => {
            state.user = {
                id: action.payload.user.id,
                email: action.payload.user.email,
                name: action.payload.user.name,
                permissions: action.payload.user.permissions,
                organization: action.payload.user.organization,
                isInternal: action.payload.user.organization?.id === config.InternalOrganization,
                filteredStores: action.payload.user.filteredStores,
            } as SessionUser;

            if (!state.hasSelectedOrganization) {
                const org = action.payload.user.organization ?? {
                    id: state.user.organization?.id ?? '',
                    name: state.user.organization?.name ?? '',
                    blockInvites: state.user.organization?.blockInvites ?? false,
                    maxApiKeys: state.user.organization?.maxApiKeys ?? 2,
                };
                state.selectedOrganization = { ...org, connectedStores: state.availableStores };
            }

            state.nextRefresh = action.payload.nextRefresh;
            state.isFetchingUser = false;
        },
        userErrorOccured: (state, action: PayloadAction<ResponseError>) => {
            state.globalError = action.payload;
            state.isFetchingUser = false;
            state.failedFetchingUser = true;
        },
        userRefreshErrorOccurred: (state, action: PayloadAction<ResponseError>) => {
            state.globalError = action.payload;
            state.isFetchingUser = false;
        },
        setSelectedOrganizationStarted: state => {
            state.selectedOrganizationPending = true;
        },
        setSelectedOrganizationSucceeded: (state, action: PayloadAction<Organization>) => {
            state.selectedOrganization = action.payload;
            state.selectedOrganizationPending = false;
            state.hasSelectedOrganization = true;
        },
        setSelectedOrganizationFailed: state => {
            state.selectedOrganization = state.user?.organization ?? {
                id: '',
                name: '',
                blockInvites: false,
                maxApiKeys: 2,
            };
            state.selectedOrganizationPending = false;
            state.hasSelectedOrganization = false;
        },
        isFetchingUser: (state, action: PayloadAction<boolean>) => {
            state.isFetchingUser = action.payload;
            state.failedFetchingUser = false;
        },
        getOrganizationStarted: state => {
            state.isLoadingOrganization = true;
        },
        getOrganizationSucceeded: (state, action: PayloadAction<Organization>) => {
            state.availableStores = action.payload.connectedStores;
            state.isLoadingOrganization = false;
            if (!state.hasSelectedOrganization)
                state.selectedOrganization.connectedStores = action.payload.connectedStores;

            // Internal users load other organizations too; the setup is only for the user's own.
            if (action.payload.id === state.user?.organization?.id)
                state.guidedSetup = {
                    isLoaded: true,
                    shouldRedirectToGetStarted: !!action.payload.shouldRedirectToGetStarted,
                    setupSteps: action.payload.setupSteps,
                };
        },
        getOrganizationFailed: state => {
            state.isLoadingOrganization = false;
            state.availableStores = [];
            // A failed load never locks anyone in or out of the setup; what was loaded before is kept.
            state.guidedSetup.isLoaded = true;
        },
        showProcessingNotComplete: (state, action: PayloadAction<boolean>) => {
            state.showProcessingNotCompletedMessage = action.payload;
        },
        setCesSurvey: (state, action: PayloadAction<{ survey: CesSurvey; key: string }>) => {
            state.cesSurveys = {
                ...(state.cesSurveys ?? {}),
                [action.payload.key]: {
                    ...((state.cesSurveys ?? {})[action.payload.key] ?? {}),
                    ...action.payload.survey,
                },
            };
        },
    },
});

export const {
    userAuthenticated,
    userLoggedOut,
    getUserSucceeded,
    userErrorOccured,
    userRefreshErrorOccurred,
    setSelectedOrganizationStarted,
    setSelectedOrganizationSucceeded,
    setSelectedOrganizationFailed,
    isFetchingUser,
    getOrganizationStarted,
    getOrganizationSucceeded,
    getOrganizationFailed,
    showProcessingNotComplete,
    setCesSurvey,
} = sessionSlice.actions;

export default sessionSlice.reducer;
